import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { RefreshCw, Plus, Minus, Trash2, Package, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { useStableSearch } from '@/hooks/useStableSearch';
import { formatJpWithAlias } from '@/utils/partDisplay';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Bangladesh Quotation Modal
 * Creates quotations for Bangladesh export customers with export pricing
 */
const BangladeshQuotationModal = ({ 
  isOpen, 
  onClose, 
  onQuotationCreated, 
  token,
  preSelectedCustomer = null
}) => {
  const [step, setStep] = useState(preSelectedCustomer ? 2 : 1);
  const [customers, setCustomers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(preSelectedCustomer);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [partSearch, setPartSearch] = useState('');
  const [items, setItems] = useState([]);
  const [packingCharge, setPackingCharge] = useState(0);
  const [courierCharge, setCourierCharge] = useState(0);
  const [notes, setNotes] = useState('');
  const [validityDays, setValidityDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [specialPrices, setSpecialPrices] = useState({});

  // Stable part search with debounce + abort + verification
  const partSearchFn = useCallback(async (query, signal) => {
    const response = await fetch(
      `${API_URL}/api/categories/All%20Products/parts?search=${encodeURIComponent(query)}&limit=20&include_inactive_brands=1&include_admin_only=true`,
      { headers: { 'Authorization': `Bearer ${token}` }, signal }
    );
    const data = await response.json();
    const parts = data?.parts || data || [];
    return Array.isArray(parts) ? parts : [];
  }, [token]);

  const { results: searchResults, isSearching: searching, setQuery: doPartSearch } = useStableSearch(partSearchFn, 500);

  useEffect(() => { doPartSearch(partSearch); }, [partSearch, doPartSearch]);  // customer_id -> jp_part_no -> special_price

  // Fetch special prices for selected customer
  const fetchSpecialPrices = useCallback(async (customerId) => {
    if (!customerId) return;
    try {
      const response = await axios.get(`${API_URL}/api/price-requests/admin/special-prices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const allPrices = response.data.special_prices || [];
      // Filter for this customer and create lookup map
      const customerPrices = {};
      allPrices
        .filter(sp => sp.customer_id === customerId)
        .forEach(sp => {
          customerPrices[sp.jp_part_no] = sp.special_price;
        });
      setSpecialPrices(customerPrices);
      if (Object.keys(customerPrices).length > 0) {
        toast.success(`Found ${Object.keys(customerPrices).length} saved handshake prices`);
      }
    } catch (error) {
      console.error('Error fetching special prices:', error);
    }
  }, [token]);

  // Update selectedCustomer when preSelectedCustomer changes
  useEffect(() => {
    if (preSelectedCustomer) {
      setSelectedCustomer(preSelectedCustomer);
      setStep(2);
      fetchSpecialPrices(preSelectedCustomer.customer_id);
    }
  }, [preSelectedCustomer, fetchSpecialPrices]);

  // Fetch customers and agents
  useEffect(() => {
    if (isOpen) {
      fetchCustomers();
      fetchAgents();
    }
  }, [isOpen]);

  const fetchCustomers = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/export-orders/export-customers?country=bangladesh&search=${customerSearch}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      setCustomers(data.customers || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await fetch(`${API_URL}/api/export-orders/export-agents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAgents(data.agents || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const addItem = (product) => {
    const existingIndex = items.findIndex(i => i.jp_part_no === product.jp_part_no);
    
    // Get handshake price: special price > MRP
    const handshakePrice = specialPrices[product.jp_part_no] || product.mrp;
    const hasSpecialPrice = !!specialPrices[product.jp_part_no];
    
    if (existingIndex >= 0) {
      const newItems = [...items];
      newItems[existingIndex].quantity += 1;
      newItems[existingIndex].subtotal = newItems[existingIndex].quantity * newItems[existingIndex].shake_hand_price;
      setItems(newItems);
    } else {
      setItems([...items, {
        jp_part_no: product.jp_part_no,
        part_name: product.part_name,
        oe_part_no: product.oe_part_no || '',
        mrp: product.mrp,
        shake_hand_price: handshakePrice,
        quantity: 1,
        subtotal: handshakePrice,
        has_special_price: hasSpecialPrice
      }]);
      if (hasSpecialPrice) {
        toast.success(`Using saved handshake price ₹${handshakePrice.toLocaleString()} for ${product.jp_part_no}`);
      }
    }
    setPartSearch('');
  };

  const updateItemPrice = (index, price) => {
    const newItems = [...items];
    newItems[index].shake_hand_price = parseFloat(price) || 0;
    newItems[index].subtotal = newItems[index].quantity * newItems[index].shake_hand_price;
    setItems(newItems);
  };

  const updateItemQty = (index, qty) => {
    const newItems = [...items];
    newItems[index].quantity = parseInt(qty) || 1;
    newItems[index].subtotal = newItems[index].quantity * newItems[index].shake_hand_price;
    setItems(newItems);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + item.subtotal, 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + parseFloat(packingCharge || 0) + parseFloat(courierCharge || 0);
  };

  const handleSubmit = async () => {
    if (submitting || loading) return;
    
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }
    if (items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    setSubmitting(true);
    setLoading(true);
    try {
      // Create Bangladesh quotation via the quotations API
      const response = await axios.post(`${API_URL}/api/quotations/bangladesh`, {
        customer_id: selectedCustomer.customer_id,
        items: items.map(item => ({
          jp_part_no: item.jp_part_no,
          part_name: item.part_name,
          oe_part_no: item.oe_part_no,
          mrp: item.mrp,
          quantity: item.quantity,
          price: item.shake_hand_price,
          discounted_price: item.shake_hand_price,
          subtotal: item.subtotal
        })),
        packing_charge: parseFloat(packingCharge) || 0,
        courier_charge: parseFloat(courierCharge) || 0,
        export_agent_id: selectedAgent && selectedAgent !== 'none' ? selectedAgent : null,
        notes: notes,
        validity_days: validityDays,
        private_marka: selectedCustomer.private_marka
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.quotation_id) {
        toast.success(`Bangladesh quotation ${response.data.quotation_id} created successfully`);
        onQuotationCreated?.(response.data);
        handleClose();
      } else {
        toast.error('Failed to create quotation');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating quotation');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(preSelectedCustomer ? 2 : 1);
    setSelectedCustomer(preSelectedCustomer);
    setItems([]);
    setPackingCharge(0);
    setCourierCharge(0);
    setNotes('');
    setPartSearch('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-xl">🇧🇩</span>
            <FileText className="w-5 h-5 text-green-600" />
            Create Bangladesh Export Quotation {preSelectedCustomer ? '' : `- Step ${step}/2`}
          </DialogTitle>
        </DialogHeader>

        {step === 1 && !preSelectedCustomer && (
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium">Export Agent (Optional)</label>
                <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                  <SelectTrigger><SelectValue placeholder="Select agent..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Agent</SelectItem>
                    {agents.map(agent => (
                      <SelectItem key={agent.customer_id} value={agent.customer_id}>
                        {agent.company_name || agent.name} ({agent.agent_markup_percentage}%)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Search Bangladesh Customer</label>
              <Input
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  fetchCustomers();
                }}
                placeholder="Search by name, company, or mobile..."
              />
            </div>

            <div className="border rounded-md max-h-64 overflow-y-auto">
              {customers.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No Bangladesh customers found.
                </div>
              ) : (
                customers.map(cust => (
                  <div
                    key={cust.customer_id}
                    className={`p-3 border-b cursor-pointer hover:bg-green-50 ${
                      selectedCustomer?.customer_id === cust.customer_id ? 'bg-green-100 border-green-300' : ''
                    }`}
                    onClick={() => {
                      setSelectedCustomer(cust);
                      fetchSpecialPrices(cust.customer_id);
                    }}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-medium">{cust.company_name || cust.name}</div>
                        <div className="text-sm text-gray-500">
                          +{cust.country_code || '880'} {cust.mobile}
                        </div>
                      </div>
                      {cust.private_marka && (
                        <Badge variant="outline" className="bg-green-50">{cust.private_marka}</Badge>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button 
                onClick={() => setStep(2)} 
                disabled={!selectedCustomer}
                className="bg-green-600 hover:bg-green-700"
              >
                Next: Add Items
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-green-50 p-3 rounded-md border border-green-200">
              <div className="flex justify-between items-center">
                <div>
                  <div className="font-medium text-green-800">{selectedCustomer?.company_name || selectedCustomer?.name}</div>
                  <div className="text-sm text-green-600">
                    +{selectedCustomer?.country_code || '880'} {selectedCustomer?.mobile} | 
                    Private Marka: {selectedCustomer?.private_marka || 'N/A'}
                  </div>
                </div>
                <Badge className="bg-green-600">Bangladesh Export Quotation</Badge>
              </div>
            </div>

            {/* Agent Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Export Agent (Optional)</label>
                <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                  <SelectTrigger><SelectValue placeholder="Select agent..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Agent</SelectItem>
                    {agents.map(agent => (
                      <SelectItem key={agent.customer_id} value={agent.customer_id}>
                        {agent.company_name || agent.name} ({agent.agent_markup_percentage}%)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Quotation Validity (Days)</label>
                <Input
                  type="number"
                  value={validityDays}
                  onChange={(e) => setValidityDays(parseInt(e.target.value) || 7)}
                  min="1"
                  max="90"
                />
              </div>
            </div>

            {/* Part Search */}
            <div>
              <label className="text-sm font-medium">Search Parts</label>
              <div className="relative">
                <Input
                  value={partSearch}
                  onChange={(e) => setPartSearch(e.target.value)}
                  placeholder="Search by part number or name..."
                />
                {searching && (
                  <RefreshCw className="absolute right-3 top-3 h-4 w-4 animate-spin text-gray-400" />
                )}
              </div>
              
              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mt-2 border rounded-md max-h-48 overflow-y-auto bg-white shadow-lg">
                  {searchResults.map(product => (
                    <div
                      key={product.jp_part_no}
                      className="p-2 border-b hover:bg-green-50 cursor-pointer flex justify-between items-center"
                      onClick={() => addItem(product)}
                    >
                      <div>
                        <div className="font-medium text-sm">{formatJpWithAlias(product)}</div>
                        <div className="text-xs text-gray-500">{product.part_name}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-green-600">₹{product.mrp}</div>
                        <Plus className="h-4 w-4 text-green-500" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Items List */}
            <div className="border rounded-md">
              <div className="bg-gray-100 p-2 font-medium text-sm grid grid-cols-12 gap-2">
                <div className="col-span-3">Part No</div>
                <div className="col-span-3">Part Name</div>
                <div className="col-span-2 text-center">Handshake ₹</div>
                <div className="col-span-2 text-center">Qty</div>
                <div className="col-span-1 text-right">Subtotal</div>
                <div className="col-span-1"></div>
              </div>
              
              <div className="max-h-48 overflow-y-auto">
                {items.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    <Package className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    No items added yet. Search and add parts above.
                  </div>
                ) : (
                  items.map((item, idx) => (
                    <div key={idx} className="p-2 border-b grid grid-cols-12 gap-2 items-center text-sm">
                      <div className="col-span-3">
                        <div className="font-medium">{formatJpWithAlias(item)}</div>
                        {item.has_special_price && (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700">Saved Price</Badge>
                        )}
                      </div>
                      <div className="col-span-3 text-xs text-gray-600 truncate" title={item.part_name}>{item.part_name}</div>
                      <div className="col-span-2">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">₹</span>
                          <Input
                            type="number"
                            value={item.shake_hand_price}
                            onChange={(e) => updateItemPrice(idx, e.target.value)}
                            className={`h-8 text-center pl-5 ${item.has_special_price ? 'border-green-400 bg-green-50' : ''}`}
                            placeholder="Handshake Price"
                          />
                        </div>
                        <div className="text-xs text-gray-400 text-center">MRP: ₹{item.mrp}</div>
                      </div>
                      <div className="col-span-2 flex items-center justify-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => updateItemQty(idx, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItemQty(idx, e.target.value)}
                          className="h-8 w-14 text-center"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => updateItemQty(idx, item.quantity + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="col-span-1 text-right font-medium">₹{item.subtotal.toLocaleString()}</div>
                      <div className="col-span-1 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          onClick={() => removeItem(idx)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Charges Section */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Packing Charge</label>
                <Input
                  type="number"
                  value={packingCharge}
                  onChange={(e) => setPackingCharge(e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Courier Charge</label>
                <Input
                  type="number"
                  value={courierCharge}
                  onChange={(e) => setCourierCharge(e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium">Notes</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Quotation notes..."
                rows={2}
              />
            </div>

            {/* Total Summary */}
            <div className="bg-green-50 border border-green-200 p-3 rounded-md space-y-1">
              <div className="flex justify-between text-sm">
                <span>Subtotal:</span>
                <span>₹{calculateSubtotal().toLocaleString('en-IN')}</span>
              </div>
              {parseFloat(packingCharge) > 0 && (
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Packing Charge:</span>
                  <span>₹{parseFloat(packingCharge).toLocaleString('en-IN')}</span>
                </div>
              )}
              {parseFloat(courierCharge) > 0 && (
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Courier Charge:</span>
                  <span>₹{parseFloat(courierCharge).toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg border-t pt-1">
                <span>Total:</span>
                <span className="text-green-700">₹{calculateTotal().toLocaleString('en-IN')}</span>
              </div>
            </div>

            <DialogFooter>
              {!preSelectedCustomer && (
                <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              )}
              <Button variant="outline" onClick={handleClose}>Cancel</Button>
              <Button 
                onClick={handleSubmit} 
                disabled={items.length === 0 || loading}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
                Create Bangladesh Quotation
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BangladeshQuotationModal;
